import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useAsyncFeedbackStore } from "@/shared/feedback/asyncFeedbackStore";
import { getApiErrorMessage } from "@/shared/lib/utils";

export type AsyncActionOptions = {
  loading?: string;
  /** Mensagem verde no canto superior direito. `false` desliga. */
  success?: string | false;
  /** Mensagem vermelha no canto superior direito. `false` desliga. */
  error?: string | false;
  invalidateKeys?: readonly (readonly string[])[];
  onSuccess?: () => void;
};

const DEFAULT_SUCCESS = "Gravação realizada com sucesso.";
const DEFAULT_ERROR = "Não foi possível salvar. Tente novamente.";

export function useAsyncAction() {
  const queryClient = useQueryClient();
  const setOperationLabel = useAsyncFeedbackStore((s) => s.setOperationLabel);
  const pushToast = useAsyncFeedbackStore((s) => s.pushToast);
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    async <T,>(fn: () => Promise<T>, options: AsyncActionOptions = {}): Promise<T | null> => {
      const {
        loading = "Salvando…",
        success = DEFAULT_SUCCESS,
        error = DEFAULT_ERROR,
        invalidateKeys,
        onSuccess,
      } = options;

      setBusy(true);
      setOperationLabel(loading);
      try {
        const result = await fn();
        if (success !== false) {
          pushToast("success", success === undefined ? DEFAULT_SUCCESS : success);
        }
        if (invalidateKeys?.length) {
          for (const key of invalidateKeys) {
            await queryClient.invalidateQueries({ queryKey: [...key] });
          }
        }
        onSuccess?.();
        return result;
      } catch (e) {
        if (error !== false) {
          const msg = typeof error === "string" ? error : getApiErrorMessage(e, DEFAULT_ERROR);
          pushToast("error", msg);
        }
        return null;
      } finally {
        setOperationLabel(null);
        setBusy(false);
      }
    },
    [queryClient, pushToast, setOperationLabel],
  );

  return { run, busy };
}
